import { computed, type ComputedRef } from 'vue';
import { useQuery, keepPreviousData } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { getActiveConversations, listTickets } from './api.pleased';
import type { ActiveTicket, ListTicketDetail } from './models.pleased';
import { getActiveTicketLatestCommentPreview } from '@/common/conversations/getActiveTicketLatestCommentPreview';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';

/** Maps self/active ticket payload to list shape for conversations UI. */
const mapActiveTicketToListTicketDetail = (ticket: ActiveTicket): ListTicketDetail => {
  const preview = getActiveTicketLatestCommentPreview(ticket);
  return {
    id: ticket.id,
    subject: ticket.subject,
    emailSubject: ticket.emailSubject,
    status: ticket.status,
    createdAt: new Date(ticket.createdAt),
    updatedAt: new Date(ticket.updatedAt),
    updatedBy: 0,
    priority: '',
    requesterId: 0,
    assigneeId: 0,
    sourceId: 0,
    brandId: 0,
    requesterName: '',
    requesterPhone: '',
    requesterEmail: '',
    agentName: '',
    agentEmail: '',
    new: false,
    createdBy: 0,
    ...(preview ? { lastMessagePreview: preview } : {}),
  };
};

export interface ConversationsQueryParams {
  requesterId: number | string;
  page: number;
  limit?: number;
}

/**
 * TanStack Query hook to fetch open/active conversations via `/support/tickets/self` (same as getActiveConversations).
 */
export const useOpenConversations = (params: ComputedRef<ConversationsQueryParams | null>) => {
  const mockedFlowStore = useMockedFlowStore();
  const { isActive: isMockedFlowActive, openConversations: mockedOpenConversations } = storeToRefs(mockedFlowStore);

  return useQuery({
    queryKey: computed(() => [
      'conversations-open',
      params.value?.requesterId,
      params.value?.page,
      params.value?.limit,
    ]),
    queryFn: async () => {
      if (isMockedFlowActive.value && mockedOpenConversations.value) {
        const full = mockedOpenConversations.value;
        if (!params.value) return full;
        const limit = params.value.limit ?? 5;
        const offset = (params.value.page - 1) * limit;
        return {
          data: full.data.slice(offset, offset + limit),
          total: full.total,
        };
      }

      if (!params.value) {
        throw new Error('Params are required');
      }

      if (!params.value.requesterId) {
        return {
          data: [],
          total: 0,
        };
      }

      const limit = params.value.limit || 5;
      const offset = (params.value.page - 1) * limit;
      const { tickets, total } = await getActiveConversations({
        onlyActive: true,
        limit,
        offset,
      });
      return {
        data: tickets.map(mapActiveTicketToListTicketDetail),
        total,
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    retry: true,
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 60_000),
  });
};

/**
 * TanStack Query hook to fetch closed/resolved conversations
 * Fetches tickets with RESOLVED or CLOSED status sorted by UPDATED_AT
 * Fetches both statuses in parallel and concatenates the results
 */
export const useClosedOrResolvedConversations = (params: ComputedRef<ConversationsQueryParams | null>) => {
  const mockedFlowStore = useMockedFlowStore();
  const { isActive: isMockedFlowActive, closedConversations: mockedClosedConversations } = storeToRefs(mockedFlowStore);

  return useQuery({
    queryKey: computed(() => [
      'conversations-not-open',
      params.value?.requesterId,
      params.value?.page,
      params.value?.limit,
    ]),
    queryFn: async () => {
      if (isMockedFlowActive.value && mockedClosedConversations.value) {
        const full = mockedClosedConversations.value;
        if (!params.value) return full;
        const limit = params.value.limit ?? 5;
        const offset = (params.value.page - 1) * limit;
        return {
          data: full.data.slice(offset, offset + limit),
          total: full.total,
        };
      }

      if (!params.value) {
        throw new Error('Params are required');
      }

      if (!params.value.requesterId) {
        return {
          data: [],
          total: 0,
        };
      }

      const perStatusLimit = params.value.limit || 5;
      const perStatusOffset = (params.value.page - 1) * perStatusLimit;

      // Fetch RESOLVED and CLOSED tickets in parallel
      const [resolvedResponse, closedResponse] = await Promise.all([
        listTickets({
          requesterId: params.value.requesterId,
          limit: perStatusLimit,
          offset: perStatusOffset,
          status: 'RESOLVED',
          sortBy: 'UPDATED_AT',
        }),
        listTickets({
          requesterId: params.value.requesterId,
          limit: perStatusLimit,
          offset: perStatusOffset,
          status: 'CLOSED' as any,
          sortBy: 'UPDATED_AT',
        }),
      ]);

      // Concatenate the data arrays and sum the totals
      return {
        data: [...(resolvedResponse.data || []), ...(closedResponse.data || [])],
        total: Math.max(resolvedResponse.total || 0, closedResponse.total || 0),
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    retry: true,
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 60_000),
  });
};
